import React, {useState, useEffect} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import DropzoneArea from 'components/files/dropzone/DropzoneArea';
import get from 'lodash.get';
import {
    Box,
    Grid,
    FormControl,
    FormLabel,
    FormHelperText,
    CircularProgress,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import usePostDocument from 'hooks/usePostDocument';
import {useFormPersistence} from 'components/forms/hooks';
import {isTouch} from 'utils/device';
import useGetFiles from 'hooks/useGetFiles';

const fileTypes = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/bmp',
    'application/pdf',
];

const useStyles = makeStyles({
    dropzoneBox: {
        margin: '1em 0',
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        flexWrap: 'wrap',
        flexDirection: 'column',
        marginBottom: '1em',
    },
});

const ImageUpload = ({value, onChange, name, limit = 12}) => {
    const classes = useStyles();
    const {files, isLoading} = useGetFiles(
        value?.user_images?.map(image => image.id)
    );

    const message = isTouch()
        ? 'Tap here to select image'
        : 'Click or drag image here';

    return (
        <Box className={classes.dropzoneBox}>
            {isLoading ? <CircularProgress /> : null}
            <DropzoneArea
                initialFiles={files?.map(image => image.signed_url)}
                acceptedFiles={fileTypes}
                dropzoneText={message}
                onChange={onChange}
                filesLimit={limit}
                showPreviewsInDropzone={false}
                showPreviews={true}
                previewText=''
                maxFileSize={50000000}
                inputProps={{
                    'data-cy': `form-field-${name}`,
                }}
            />
        </Box>
    );
};

const ImageUploadField = ({
    label,
    name,
    required,
    limit,
    documentType,
    setOnSubmit,
    setUploading,
}) => {
    // Posting stuffs
    const {postNewManualDocument} = usePostDocument();
    const [postError, setPostError] = useState(null);

    // Form stuffs
    const {control, errors, setValue} = useFormContext();
    const error = get(errors, name, postError);

    // Etc
    const {state} = useFormPersistence();
    const [files, setFiles] = useState([]);

    const getPostData = () => {
        const data = new FormData();
        data.append('document_type', documentType);
        for (const [index, file] of files.entries()) {
            // need to get user_document_id
            file.page_number = index + 1;
            data.append('user_document', file);
            data.append('page_number', index + 1);
        }
        return data;
    };

    useEffect(() => {
        async function postFiles() {
            let success = false;
            try {
                if (files.length) {
                    setUploading(true);
                    const result = await postNewManualDocument(getPostData());
                    // Replace the form state with the response
                    setValue(name, result.data, {shouldValidate: true});
                }
                success = true;
            } catch (e) {
                setPostError(e);
            } finally {
                setUploading(false);
                return success;
            }
        }
        setOnSubmit({
            post: postFiles,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setOnSubmit, files]);

    const onChange = data => {
        setFiles(data);
    };

    return (
        <Grid container direction='column'>
            <FormControl fullWidth error={!!error}>
                <FormLabel id={`${name}-label`} required={required}>
                    {label}
                </FormLabel>
                <Controller
                    render={p =>
                        files && (
                            <ImageUpload
                                value={get(state, name)}
                                onChange={onChange}
                                limit={limit}
                                name={name}
                            />
                        )
                    }
                    name={name}
                    control={control}
                />
                <FormHelperText>{error?.message}</FormHelperText>
            </FormControl>
        </Grid>
    );
};

export default ImageUploadField;
